import { db } from '../../../../config/firebaseConfig';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';

// ==================== PROJECT MANAGEMENT FUNCTIONS ====================

/**
 * Save a new project to Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} projectId - Project unique ID
 * @param {Object} projectData - Project data (title, overview, images with descriptions)
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if save fails
 */
export const saveProject = async (userId, projectId, projectData) => {
  try {
    const projectRef = doc(db, 'users', userId, 'projects', projectId);

    await setDoc(projectRef, {
      ...projectData,
      id: projectId,
      createdAt: new Date(),
      updatedAt: new Date(),
      isPublished: false,
      publicRefsCount: 0,
    }, { merge: true });

    return {
      success: true,
      message: `Project "${projectData.title}" saved successfully`,
    };
  } catch (error) {
    console.error('Error saving project:', error.message);
    throw new Error(`Failed to save project: ${error.message}`);
  }
};

/**
 * Load a specific project from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} projectId - Project ID to load
 * @returns {Promise<Object|null>} - Project data or null if not found
 * @throws {Error} - Firestore error if load fails
 */
export const loadProject = async (userId, projectId) => {
  try {
    const projectRef = doc(db, 'users', userId, 'projects', projectId);
    const docSnap = await getDoc(projectRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
      };
    }

    return null;
  } catch (error) {
    console.error('Error loading project:', error.message);
    throw new Error(`Failed to load project: ${error.message}`);
  }
};

/**
 * Load all projects for a specific user
 * 
 * @param {string} userId - User's unique ID
 * @returns {Promise<Array>} - Array of project objects with metadata
 * @throws {Error} - Firestore error if load fails
 */
export const loadUserProjects = async (userId) => {
  try {
    const projectsRef = collection(db, 'users', userId, 'projects');
    const querySnapshot = await getDocs(projectsRef);

    const projects = [];
    querySnapshot.forEach((doc) => {
      projects.push({
        ...doc.data(),
        id: doc.id,
      });
    });

    // Sort by updatedAt in descending order (newest first)
    projects.sort((a, b) => {
      const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt);
      const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt);
      return dateB - dateA;
    });

    return projects;
  } catch (error) {
    console.error('Error loading projects:', error.message);
    throw new Error(`Failed to load projects: ${error.message}`);
  }
};

/**
 * Update an existing project in Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} projectId - Project ID to update
 * @param {Object} updateData - Partial data to update
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if update fails
 */
export const updateProject = async (userId, projectId, updateData) => {
  try {
    const projectRef = doc(db, 'users', userId, 'projects', projectId);

    await updateDoc(projectRef, {
      ...updateData,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: 'Project updated successfully',
    };
  } catch (error) {
    console.error('Error updating project:', error.message);
    throw new Error(`Failed to update project: ${error.message}`);
  }
};

/**
 * Delete a specific project from Firestore
 * 
 * @param {string} userId - User's unique ID
 * @param {string} projectId - Project ID to delete
 * @returns {Promise<Object>} - Success status and message
 * @throws {Error} - Firestore error if delete fails
 */
export const deleteProject = async (userId, projectId) => {
  try {
    const projectRef = doc(db, 'users', userId, 'projects', projectId);
    await deleteDoc(projectRef);

    return {
      success: true,
      message: 'Project deleted successfully',
    };
  } catch (error) {
    console.error('Error deleting project:', error.message);
    throw new Error(`Failed to delete project: ${error.message}`);
  }
};